import React, { useState } from 'react';
import { ChevronDownIcon } from 'lucide-react';
import { usePricing } from '../../contexts/PricingContext';
import { useFinancialModel } from '../../hooks/useFinancialModel';
import { inputsFromDeck } from '../../utils/financialModel';
import { managerView } from '../../utils/commissions';
import { getRevenueDelay } from '../../utils/roiCalculations';
import type { Inputs } from '../../utils/roiCalculations';
import { applyBasePreset, reconcilePresets, togglePlusFive } from '../../utils/retentionPresets';
import { ControlNumberField } from './ControlNumberField';
const sectionLabel = 'text-[10px] font-bold uppercase tracking-[0.14em] text-slate-500';
const fieldLabel = 'text-[12px] text-slate-400';
type NumericKey = 'ticket' | 'gpm' | 'salesVetMonthly' | 'pullthrough' | 'pctLaunched' | 'pct30' | 'pct90' | 'pct1y';
interface FieldDef {
  key: NumericKey;
  label: string;
  prefix?: string;
  suffix?: string;
  max?: number;
  allowDecimal?: boolean;
}
const BUSINESS: FieldDef[] = [{
  key: 'ticket',
  label: 'Average ticket',
  prefix: '$'
}, {
  key: 'gpm',
  label: 'Margin after comm.',
  prefix: '',
  suffix: '%',
  max: 100
}, {
  key: 'salesVetMonthly',
  label: 'Sales / veteran / mo',
  prefix: '',
  max: 100,
  allowDecimal: true
}, {
  key: 'pullthrough',
  label: 'Pull-through',
  prefix: '',
  suffix: '%',
  max: 100
}];
const RETENTION: FieldDef[] = [{
  key: 'pctLaunched',
  label: 'Hires who launch',
  prefix: '',
  suffix: '%',
  max: 100
}, {
  key: 'pct30',
  label: 'Day 30, of launched',
  prefix: '',
  suffix: '%',
  max: 100
}, {
  key: 'pct90',
  label: 'Day 90, of day 30',
  prefix: '',
  suffix: '%',
  max: 100
}, {
  key: 'pct1y',
  label: 'Day 90 to 1 year',
  prefix: '',
  suffix: '%',
  max: 100
}];
const RAMP_KEYS = ['rampMonth1Pct', 'rampMonth2Pct', 'rampMonth3Pct'] as const;
const RAMP_DEFAULTS = [25, 50, 75];

/**
 * The model's business assumptions, editable from the Tailor panel. Edits go
 * to the same shared inputs the ROI calculator writes, so the calculator,
 * the model pages and the pricing table all stay in step.
 */
export function AssumptionControls() {
  const [open, setOpen] = useState(false);
  const {
    calculatorInputs,
    setCalculatorInputs,
    adSpend,
    cpaLow,
    cpaHigh,
    retentionPresets,
    setRetentionPresets
  } = usePricing();
  const {
    inputs
  } = useFinancialModel();
  const revenueDelay = getRevenueDelay(inputs);
  const baseInputs = () => calculatorInputs ?? inputsFromDeck({
    adSpend,
    cpaLow,
    cpaHigh
  });
  const update = (patch: Partial<Inputs>) => {
    const next = {
      ...baseInputs(),
      ...patch
    };
    setCalculatorInputs(next);
    // A typed retention value that no longer matches the preset clears it,
    // the same as in the calculator.
    const presets = reconcilePresets(next, retentionPresets);
    if (presets !== retentionPresets) setRetentionPresets(presets);
  };
  const applyPreset = (preset: 'low' | 'onTarget' | 'plusFive') => {
    const result = preset === 'plusFive' ? togglePlusFive(baseInputs(), retentionPresets) : applyBasePreset(baseInputs(), retentionPresets, preset);
    setCalculatorInputs(result.inputs);
    setRetentionPresets(result.state);
  };
  const presetButtons = [{
    key: 'low' as const,
    label: 'Low',
    active: retentionPresets.basePreset === 'low'
  }, {
    key: 'onTarget' as const,
    label: 'On Target',
    active: retentionPresets.basePreset === 'onTarget'
  }, {
    key: 'plusFive' as const,
    label: '+5%',
    active: retentionPresets.plusFive
  }];
  const renderField = (field: FieldDef) => <div key={field.key} className="flex items-center justify-between gap-2" data-id="element-1229" data-mp="33j2ndzkg7z96">
      <span className={`${fieldLabel} whitespace-nowrap`} data-id="element-1230" data-mp="2otodmqcynft9">{field.label}</span>
      <ControlNumberField label={field.label} value={inputs[field.key] as number} min={0} max={field.max} prefix={field.prefix} suffix={field.suffix} allowDecimal={field.allowDecimal} onCommit={next => update({
      [field.key]: next
    } as Partial<Inputs>)} className="w-[76px] shrink-0" data-id="element-1231" data-mp="2tt97wnh6qzzn" />
    </div>;
  return <div className="mt-4 pt-3 border-t border-slate-800" data-id="element-1232" data-mp="1tmxdkctn4595">
      <button type="button" onClick={() => setOpen(v => !v)} aria-expanded={open} className="w-full flex items-center justify-between rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1233" data-mp="1tldy2oft5y8f">
        <span className={sectionLabel} data-id="element-1234" data-mp="2hcjbkwya0efk">Assumptions</span>
        <ChevronDownIcon aria-hidden="true" className={`w-3.5 h-3.5 text-slate-500 transition-transform duration-150 ease-out ${open ? 'rotate-180' : ''}`} data-id="element-1235" data-mp="2he2terzkyxjg" />
      </button>
      {open && <div className="mt-2 space-y-1.5" data-id="element-1236" data-mp="22nn719vfhjjp">
          {BUSINESS.filter(field => !(field.key === 'gpm' && managerView(inputs))).map(renderField)}
          <div className="flex items-center justify-between gap-2" data-id="element-1237" data-mp="304u9sdhzjo7c">
            <span className={`${fieldLabel} whitespace-nowrap`} data-id="element-1238" data-mp="2vb99ufwu0k5i">Ramp, mo 1–3</span>
            <div className="flex gap-1" data-id="element-1239" data-mp="32nz48l58f08a">
              {RAMP_KEYS.map((key, i) => <ControlNumberField key={key} label={`Ramp, month ${i + 1}`} value={inputs[key] ?? RAMP_DEFAULTS[i]} min={0} max={100} prefix="" onCommit={next => update({
            [key]: next
          } as Partial<Inputs>)} className="w-[34px] !px-1" data-id="element-1240" data-mp="37lqu79mbgc9u" data-mpi={i} />)}
            </div>
          </div>
          <div className="flex items-center justify-between gap-2" data-id="element-1241" data-mp="3qjbjuiwwnevi">
            <span className={`${fieldLabel} whitespace-nowrap`} data-id="element-1242" data-mp="3vcw7bh0vgqgg">Revenue delay</span>
            <div className="flex items-center gap-1" data-id="element-1243" data-mp="4637aghtexdi">
              <button type="button" aria-label="Less revenue delay" disabled={revenueDelay <= 0} onClick={() => update({
            revenueDelayMonths: revenueDelay - 1
          })} className="w-6 h-6 rounded border border-slate-700 text-slate-300 text-sm leading-none hover:text-white hover:border-slate-500 disabled:opacity-40 disabled:pointer-events-none transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1244" data-mp="47moc9bapcgc">
                −
              </button>
              <span className="w-[40px] text-center text-[12px] font-semibold text-white tabular-nums" data-id="element-1245" data-mp="2e6wh8xbis8pf">
                {revenueDelay === 0 ? 'None' : `${revenueDelay} mo`}
              </span>
              <button type="button" aria-label="More revenue delay" disabled={revenueDelay >= 11} onClick={() => update({
            revenueDelayMonths: revenueDelay + 1
          })} className="w-6 h-6 rounded border border-slate-700 text-slate-300 text-sm leading-none hover:text-white hover:border-slate-500 disabled:opacity-40 disabled:pointer-events-none transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3]" data-id="element-1246" data-mp="2nxr5g78btidi">
                +
              </button>
            </div>
          </div>
          <div className="pt-1.5 space-y-1.5" data-id="element-1247" data-mp="xi6sa4isnoh8">
            <div className="flex items-center justify-between gap-2" data-id="element-1248" data-mp="jph7g3f04plp">
              <span className={`${fieldLabel} whitespace-nowrap`} data-id="element-1249" data-mp="onsey7htqo0r">Retention</span>
              <div className="flex gap-1" data-id="element-1250" data-mp="olo1axu4iana">
                {presetButtons.map(button => <button key={button.key} type="button" aria-pressed={button.active} onClick={() => applyPreset(button.key)} className={`rounded px-1.5 py-1 text-[11px] font-semibold whitespace-nowrap border transition-colors duration-150 ease-out focus:outline-none focus-visible:ring-2 focus-visible:ring-[#7FE0C3] ${button.active ? 'border-[#14684d] bg-[#14684d] text-white' : 'border-slate-700 text-slate-400 hover:text-white hover:border-slate-500'}`} data-id="element-1251" data-mp="9sm8xkiqap7f">
                    {button.label}
                  </button>)}
              </div>
            </div>
            {RETENTION.map(renderField)}
          </div>
          <p className="pt-1 text-[11px] leading-snug text-slate-500" data-id="element-1252" data-mp="2uwhum5aakq9w">
            Shared with the ROI calculator.
          </p>
        </div>}
    </div>;
}